import './FacilityMap.scss';
import React from 'react';

import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';

export interface FacilityMapProps {
  /** Falls back to the placeholder until a facility image can be uploaded. */
  src?: string | null;
  alt?: string;
  /** Overlay cards, pinned to the bottom of the map. */
  children?: React.ReactNode;
  className?: string;
}

/*
 * The space for the facility map: a replaceable image, cropped to a fixed
 * aspect ratio so any upload leaves the layout alone. The design's overlay
 * cards land on top of this container later.
 */
export default function FacilityMap ({ src, alt, children, className }: FacilityMapProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'ds-relative ds-w-full ds-overflow-hidden ds-bg-muted',
        // 1200 x 369.055 with a bottom border, per Figma (node 1799:29539).
        'ds-aspect-[517/159] ds-border-b ds-border-solid ds-border-border',
        className
      )}
      data-testid='facility-map'
    >
      {src
        ? (
          <img
            src={src}
            alt={alt ?? t('dashboard.facilityMapAlt')}
            /* Top-anchored, as in the design: an aerial view keeps its
             * detail up top, so crop off the bottom rather than both edges. */
            className='ds-h-full ds-w-full ds-object-cover ds-object-top'
          />
        )
        : (
          <div
            className='map-placeholder-backdrop ds-h-full ds-w-full'
            data-testid='facility-map-placeholder'
          />
        )}

      {children
        ? (
          <div
            className='ds-absolute ds-bottom-0 ds-left-0 ds-p-3.5'
            data-testid='facility-map-overlay'
          >
            {children}
          </div>
        )
        : null}
    </div>
  );
}
